import React, { useState, useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { Card, CardHeader, CardTitle, CardContent } from './ui/card';
import { Button } from './ui/button';
import {
  CreditCard, DollarSign, AlertCircle, Loader2, Users,
  CheckCircle2, Clock, XCircle, ChevronRight, ArrowLeft, X, Plus,
} from 'lucide-react';
import { paymentService } from '../services/paymentService';
import { tenantService } from '../services/tenantService';
import apiClient from '../services/authService';
import BankSetupForm, { ConnectedBankCard } from './BankSetupForm';

// ── Status helpers ────────────────────────────────────────────────────────────

const connectStatusConfig = {
  not_started: { label: 'Not Started', color: 'bg-gray-100 text-gray-700 border-gray-200' },
  onboarding:  { label: 'In Progress', color: 'bg-amber-50 text-amber-700 border-amber-200' },
  restricted:  { label: 'Needs Attention', color: 'bg-red-50 text-red-700 border-red-200' },
  active:      { label: 'Active', color: 'bg-green-50 text-green-700 border-green-200' },
  disabled:    { label: 'Disabled', color: 'bg-red-50 text-red-700 border-red-200' },
};

const achStatusConfig = {
  not_started: { label: 'Not Setup', color: 'bg-gray-100 text-gray-600', Icon: XCircle },
  pending:     { label: 'Pending',   color: 'bg-amber-100 text-amber-700', Icon: Clock },
  verified:    { label: 'Connected', color: 'bg-green-100 text-green-700', Icon: CheckCircle2 },
  failed:      { label: 'Failed',    color: 'bg-red-100 text-red-700',    Icon: XCircle },
};

const AchBadge = ({ achStatus, subscriptionActive }) => {
  if (subscriptionActive) {
    return (
      <span className="inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full bg-green-100 text-green-700">
        <CheckCircle2 className="h-3 w-3" /> ACH Active
      </span>
    );
  }
  const cfg = achStatusConfig[achStatus] || achStatusConfig.not_started;
  const Icon = cfg.Icon;
  return (
    <span className={`inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full ${cfg.color}`}>
      <Icon className="h-3 w-3" /> {cfg.label}
    </span>
  );
};

// ── Deposit account section (shown when Connect is active) ────────────────────

const DepositAccountSection = () => {
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);

  useEffect(() => {
    paymentService.getConnectBankAccount()
      .then(d => setData(d))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  if (loading) return null;

  const { bankAccount, connectStatus, remediationUrl, requirements = [] } = data || {};

  const handleSave = async (formData) => {
    const res = await paymentService.saveConnectBankAccount(formData);
    setData(res);
    return res;
  };

  return (
    <div className="space-y-2">
      <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Deposit Account</p>

      {/* Restricted / verification required banner */}
      {connectStatus === 'restricted' && remediationUrl && (
        <div className="flex items-start gap-3 p-3 bg-amber-50 border border-amber-200 rounded-lg">
          <AlertCircle className="h-4 w-4 text-amber-600 flex-shrink-0 mt-0.5" />
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-amber-800">Verification required</p>
            <p className="text-xs text-amber-700 mt-0.5">
              Stripe needs additional information before payouts can be activated.
              {requirements.length > 0 && ` Required: ${requirements.join(', ')}.`}
            </p>
            <a
              href={remediationUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-block mt-2 text-xs font-semibold text-amber-800 underline"
            >
              Complete verification →
            </a>
          </div>
        </div>
      )}

      {!editing && bankAccount ? (
        <ConnectedBankCard
          bankName={bankAccount.bankName}
          last4={bankAccount.last4}
          routingLast4={bankAccount.routingLast4}
          status={connectStatus === 'active' ? 'verified' : connectStatus}
          onUpdate={() => setEditing(true)}
        />
      ) : editing ? (
        <Card>
          <CardContent className="pt-5">
            <BankSetupForm
              submitLabel="Update Deposit Account"
              onSave={handleSave}
              onSuccess={() => setEditing(false)}
            />
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent className="pt-5">
            <BankSetupForm
              submitLabel="Connect Deposit Account"
              onSave={handleSave}
              onSuccess={() => setEditing(false)}
            />
          </CardContent>
        </Card>
      )}
    </div>
  );
};

// ── Landlord bank setup / connected card ──────────────────────────────────────

const LandlordBankSetup = ({ onConnected }) => {
  const [bankAccount, setBankAccount] = useState(null);
  const [showForm, setShowForm]       = useState(false);
  const [loading, setLoading]         = useState(true);

  useEffect(() => {
    paymentService.getConnectBankAccount()
      .then(d => {
        setBankAccount(d.bankAccount);
        setShowForm(!d.bankAccount);
      })
      .catch(() => setShowForm(true))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-gray-400" /></div>;

  return (
    <div className="max-w-md mx-auto py-10 px-4 space-y-6">
      <div className="text-center">
        <div className="w-14 h-14 bg-cyan-50 rounded-full flex items-center justify-center mx-auto mb-4">
          <CreditCard className="h-7 w-7 text-cyan-600" />
        </div>
        <h2 className="text-xl font-semibold mb-1">Deposit Account</h2>
        <p className="text-sm text-muted-foreground">
          {bankAccount ? 'Your deposit account for rent collection.' : 'Add your bank account to receive ACH rent payments.'}
        </p>
      </div>

      {bankAccount && !showForm ? (
        <div className="space-y-4">
          <ConnectedBankCard
            bankName={bankAccount.bankName}
            last4={bankAccount.last4}
            routingLast4={bankAccount.routingLast4}
            status={bankAccount.status}
            onUpdate={() => setShowForm(true)}
          />
          <Button className="w-full bg-cyan-600 hover:bg-cyan-700 text-white" onClick={onConnected}>
            Continue to NivaPay
          </Button>
        </div>
      ) : (
        <BankSetupForm
          submitLabel={bankAccount ? 'Update Bank Account' : 'Connect Bank Account'}
          onSave={async (data) => {
            const res = await paymentService.saveConnectBankAccount(data);
            setBankAccount(res.bankAccount);
            return res;
          }}
          onSuccess={() => {
            setShowForm(false);
            onConnected();
          }}
        />
      )}
    </div>
  );
};

// ── Activate ACH modal ────────────────────────────────────────────────────────

const ActivateModal = ({ tenant, onConfirm, onClose, title = 'Activate Recurring Rent' }) => {
  const [lease, setLease]           = useState(null);
  const [leaseLoading, setLLLoad]   = useState(true);
  const [paymentDay, setPaymentDay] = useState(tenant?.paymentDay || 1);
  const [loading, setLoading]       = useState(false);
  const [error, setError]           = useState('');

  useEffect(() => {
    apiClient.get('/api/leases', { params: { tenantId: tenant._id } })
      .then(r => {
        const active = (r.data || []).find(l => l.status === 'Active') || r.data?.[0] || null;
        setLease(active);
        if (active?.leaseStart) {
          setPaymentDay(new Date(active.leaseStart).getDate());
        }
      })
      .catch(() => {})
      .finally(() => setLLLoad(false));
  }, [tenant._id]);

  const fmtDate = d => d ? new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—';

  const handleConfirm = async () => {
    try {
      setLoading(true);
      setError('');
      await onConfirm(tenant._id, paymentDay);
      onClose();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to activate. Please try again.');
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-white rounded-xl shadow-xl p-6 max-w-sm w-full mx-4">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-base font-semibold">{title}</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="h-4 w-4" />
          </button>
        </div>

        {leaseLoading ? (
          <div className="flex justify-center py-6"><Loader2 className="h-5 w-5 animate-spin text-gray-400" /></div>
        ) : lease ? (
          <div className="bg-gray-50 border rounded-lg p-3 mb-4 space-y-1.5 text-sm">
            <div className="flex justify-between">
              <span className="text-muted-foreground">Monthly Rent</span>
              <span className="font-semibold text-gray-900">${lease.monthlyRent?.toLocaleString()}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Lease Start</span>
              <span className="font-medium">{fmtDate(lease.leaseStart)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Lease End</span>
              <span className="font-medium">{fmtDate(lease.leaseEnd)}</span>
            </div>
            <p className="text-xs text-muted-foreground pt-1 border-t">
              Recurring payments will stop automatically at lease end.
            </p>
          </div>
        ) : (
          <p className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded p-2 mb-4">
            No active lease found for this tenant. Create a lease before activating payments.
          </p>
        )}

        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-1">Payment Day of Month (1–28)</label>
          <input
            type="number"
            min={1}
            max={28}
            value={paymentDay}
            onChange={e => setPaymentDay(Number(e.target.value))}
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500"
          />
          <p className="text-xs text-muted-foreground mt-1">Defaulted to lease start day. Adjust if needed.</p>
        </div>

        {error && (
          <p className="text-xs text-red-600 bg-red-50 border border-red-200 rounded p-2 mb-3">{error}</p>
        )}
        <div className="flex gap-3">
          <Button variant="outline" className="flex-1" onClick={onClose}>Cancel</Button>
          <Button
            className="flex-1 bg-cyan-600 hover:bg-cyan-700 text-white"
            onClick={handleConfirm}
            disabled={loading || leaseLoading || !lease}
          >
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Activate'}
          </Button>
        </div>
      </div>
    </div>
  );
};

// ── Create Invoice modal ──────────────────────────────────────────────────────

const CreateInvoiceModal = ({ tenant, onConfirm, onClose }) => {
  const [description, setDescription] = useState('');
  const [amount, setAmount]           = useState('');
  const [category, setCategory]       = useState('Rent');
  const [loading, setLoading]         = useState(false);
  const [error, setError]             = useState('');

  const handleCreate = async () => {
    if (!description || !amount) { setError('Description and amount are required.'); return; }
    try {
      setLoading(true);
      await onConfirm({
        tenantId:  tenant._id,
        lineItems: [{ description, amount: parseFloat(amount), category }],
      });
      onClose();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to create invoice.');
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-white rounded-xl shadow-xl p-6 max-w-sm w-full mx-4">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-base font-semibold">Create Invoice</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X className="h-4 w-4" /></button>
        </div>
        <div className="space-y-3 mb-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
            <input
              value={description}
              onChange={e => setDescription(e.target.value)}
              placeholder="e.g. Late fee"
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Amount ($)</label>
            <input
              type="number"
              min={0}
              step={0.01}
              value={amount}
              onChange={e => setAmount(e.target.value)}
              placeholder="0.00"
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
            <select
              value={category}
              onChange={e => setCategory(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500"
            >
              {['Rent', 'Late Fee', 'Security Deposit', 'Damage', 'Maintenance Charge', 'Other'].map(c => (
                <option key={c}>{c}</option>
              ))}
            </select>
          </div>
        </div>
        {error && <p className="text-xs text-red-600 bg-red-50 border border-red-200 rounded p-2 mb-3">{error}</p>}
        <div className="flex gap-3">
          <Button variant="outline" className="flex-1" onClick={onClose}>Cancel</Button>
          <Button className="flex-1 bg-cyan-600 hover:bg-cyan-700 text-white" onClick={handleCreate} disabled={loading}>
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Create'}
          </Button>
        </div>
      </div>
    </div>
  );
};

// ── Record Payment modal ──────────────────────────────────────────────────────

const RecordPaymentModal = ({ tenant, invoices, onConfirm, onClose }) => {
  const unpaid = invoices.filter(inv => ['Issued', 'Partially Paid', 'Overdue'].includes(inv.status));
  const [invoiceId, setInvoiceId] = useState(unpaid[0]?._id || '');
  const [amount, setAmount]       = useState('');
  const [method, setMethod]       = useState('Cash');
  const [reference, setReference] = useState('');
  const [loading, setLoading]     = useState(false);
  const [error, setError]         = useState('');

  const handleRecord = async () => {
    if (!invoiceId || !amount) { setError('Invoice and amount are required.'); return; }
    try {
      setLoading(true);
      await onConfirm({ invoiceId, amount: parseFloat(amount), method, reference });
      onClose();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to record payment.');
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-white rounded-xl shadow-xl p-6 max-w-sm w-full mx-4">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-base font-semibold">Record Payment</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X className="h-4 w-4" /></button>
        </div>
        <div className="space-y-3 mb-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Invoice</label>
            <select
              value={invoiceId}
              onChange={e => setInvoiceId(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500"
            >
              {unpaid.length === 0 && <option value="">No outstanding invoices</option>}
              {unpaid.map(inv => (
                <option key={inv._id} value={inv._id}>
                  {inv.invoiceNumber}: ${inv.totalAmount} ({inv.status})
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Amount ($)</label>
            <input
              type="number"
              min={0}
              step={0.01}
              value={amount}
              onChange={e => setAmount(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Method</label>
            <select
              value={method}
              onChange={e => setMethod(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500"
            >
              {['Cash', 'Check', 'Other'].map(m => <option key={m}>{m}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Reference (optional)</label>
            <input
              value={reference}
              onChange={e => setReference(e.target.value)}
              placeholder="Check #, memo, etc."
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500"
            />
          </div>
        </div>
        {error && <p className="text-xs text-red-600 bg-red-50 border border-red-200 rounded p-2 mb-3">{error}</p>}
        <div className="flex gap-3">
          <Button variant="outline" className="flex-1" onClick={onClose}>Cancel</Button>
          <Button className="flex-1 bg-cyan-600 hover:bg-cyan-700 text-white" onClick={handleRecord} disabled={loading || unpaid.length === 0}>
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Record'}
          </Button>
        </div>
      </div>
    </div>
  );
};

// ── Tenant account detail panel ───────────────────────────────────────────────

const ordinalSuffix = n => ['st','nd','rd'][((n + 90) % 100 - 10) % 10 - 1] || 'th';

const TenantDetail = ({ tenant, balance, ledger, invoices, lease, connectStatus, onRefresh }) => {
  const [modal, setModal]         = useState(null); // 'activate' | 'modify' | 'invoice' | 'payment'
  const [deactivating, setDeact]  = useState(false);

  const canActivate   = connectStatus === 'active' && tenant.achStatus === 'verified' && !tenant.stripeSubscriptionId;
  const isActive      = !!tenant.stripeSubscriptionId;
  const monthlyAmount = lease?.monthlyRent ?? null;

  const handleActivate = async (tenantId, paymentDay) => {
    await paymentService.activateACH(tenantId, paymentDay);
    onRefresh();
  };

  const handleModify = async (tenantId, paymentDay) => {
    await paymentService.deactivateACH(tenant._id);
    await paymentService.activateACH(tenantId, paymentDay);
    onRefresh();
  };

  const handleDeactivate = async () => {
    if (!window.confirm(`Stop automatic payments for ${tenant.name}?`)) return;
    setDeact(true);
    try {
      await paymentService.deactivateACH(tenant._id);
      onRefresh();
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to deactivate.');
    } finally {
      setDeact(false);
    }
  };

  const handleCreateInvoice = async (data) => {
    await paymentService.createInvoice(data);
    onRefresh();
  };

  const handleRecordPayment = async (data) => {
    await paymentService.recordPayment(data);
    onRefresh();
  };

  return (
    <div className="flex-1 overflow-y-auto">
      {/* Header */}
      <div className="px-6 py-4 border-b bg-white sticky top-0">
        <div className="flex items-start justify-between gap-3">
          <div>
            <h3 className="font-semibold text-gray-900">{tenant.name}</h3>
            <p className="text-xs text-muted-foreground">{tenant.unitNumber} · {tenant.property?.name || tenant.property}</p>
          </div>
          <AchBadge achStatus={tenant.achStatus} subscriptionActive={isActive} />
        </div>
        {/* ACH status / action area */}
        {isActive ? (
          <div className="mt-4 rounded-xl border border-green-200 bg-gradient-to-r from-green-50 to-emerald-50 overflow-hidden">
            <div className="px-4 py-3 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <CheckCircle2 className="h-4 w-4 text-green-600 flex-shrink-0" />
                <span className="text-sm font-semibold text-green-800">ACH Active</span>
              </div>
              <div className="text-right">
                {monthlyAmount != null && (
                  <p className="text-base font-bold text-green-900">${monthlyAmount.toLocaleString()}<span className="text-xs font-normal text-green-700">/mo</span></p>
                )}
                <p className="text-xs text-green-700">{tenant.paymentDay}{ordinalSuffix(tenant.paymentDay)} of each month</p>
              </div>
            </div>
            <div className="border-t border-green-200 px-4 py-2 flex flex-wrap gap-2 bg-white/50">
              <Button size="sm" variant="ghost" className="text-xs h-7 text-gray-600 hover:text-gray-900" onClick={() => setModal('modify')}>
                Modify Schedule
              </Button>
              <Button size="sm" variant="ghost" className="text-xs h-7 text-red-600 hover:text-red-800 hover:bg-red-50" onClick={handleDeactivate} disabled={deactivating}>
                {deactivating ? <Loader2 className="h-3 w-3 animate-spin" /> : 'Deactivate'}
              </Button>
            </div>
          </div>
        ) : (
          <div className="mt-3">
            {canActivate && (
              <Button size="sm" className="bg-cyan-600 hover:bg-cyan-700 text-white text-xs" onClick={() => setModal('activate')}>
                Activate Recurring Rent
              </Button>
            )}
            {!canActivate && tenant.achStatus !== 'verified' && (
              <span className="text-xs text-muted-foreground italic py-1">Tenant must connect bank first</span>
            )}
          </div>
        )}

        {/* Invoice / payment buttons always visible */}
        <div className="flex gap-2 mt-3">
          <Button size="sm" variant="outline" className="text-xs" onClick={() => setModal('invoice')}>
            <Plus className="h-3 w-3 mr-1" />Invoice
          </Button>
          <Button size="sm" variant="outline" className="text-xs" onClick={() => setModal('payment')}>
            Record Payment
          </Button>
        </div>
      </div>

      {/* Balance summary */}
      {balance && (
        <div className="px-6 py-4 bg-gray-50 border-b">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <p className="text-xs text-muted-foreground">Outstanding</p>
              <p className={`text-lg font-bold ${balance.outstanding > 0 ? 'text-red-600' : 'text-green-600'}`}>
                ${(balance.outstanding || 0).toLocaleString()}
              </p>
            </div>
            {balance.lastPayment && (
              <div>
                <p className="text-xs text-muted-foreground">Last Payment</p>
                <p className="text-lg font-bold">${balance.lastPayment.amount?.toLocaleString()}</p>
                <p className="text-xs text-muted-foreground">
                  {new Date(balance.lastPayment.date).toLocaleDateString()}
                </p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Ledger */}
      <div className="p-6">
        <h4 className="text-sm font-semibold text-gray-700 mb-3">Account Ledger</h4>
        {!ledger || ledger.entries.length === 0 ? (
          <p className="text-sm text-muted-foreground text-center py-6">No transactions yet.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b">
                  <th className="text-left py-2 font-medium text-gray-600 text-xs">Date</th>
                  <th className="text-left py-2 font-medium text-gray-600 text-xs">Description</th>
                  <th className="text-right py-2 font-medium text-gray-600 text-xs">Amount</th>
                  <th className="text-right py-2 font-medium text-gray-600 text-xs">Balance</th>
                </tr>
              </thead>
              <tbody>
                {ledger.entries.map((entry, i) => (
                  <tr key={i} className="border-b last:border-0">
                    <td className="py-2 text-xs text-gray-500">
                      {new Date(entry.date).toLocaleDateString()}
                    </td>
                    <td className="py-2 text-gray-700">{entry.description}</td>
                    <td className={`py-2 text-right font-medium ${entry.amount < 0 ? 'text-red-600' : 'text-green-600'}`}>
                      {entry.amount < 0 ? '-' : '+'}${Math.abs(entry.amount).toLocaleString()}
                    </td>
                    <td className={`py-2 text-right font-semibold text-xs ${entry.runningBalance > 0 ? 'text-red-600' : 'text-green-600'}`}>
                      ${Math.abs(entry.runningBalance).toLocaleString()}
                      {entry.runningBalance > 0 ? ' owed' : ''}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Modals */}
      {modal === 'activate' && (
        <ActivateModal tenant={tenant} onConfirm={handleActivate} onClose={() => setModal(null)} />
      )}
      {modal === 'modify' && (
        <ActivateModal tenant={tenant} onConfirm={handleModify} onClose={() => setModal(null)} title="Modify Payment Schedule" />
      )}
      {modal === 'invoice' && (
        <CreateInvoiceModal tenant={tenant} onConfirm={handleCreateInvoice} onClose={() => setModal(null)} />
      )}
      {modal === 'payment' && (
        <RecordPaymentModal
          tenant={tenant}
          invoices={invoices}
          onConfirm={handleRecordPayment}
          onClose={() => setModal(null)}
        />
      )}
    </div>
  );
};

// ── Main RentCollection component ─────────────────────────────────────────────

const FILTERS = ['All', 'ACH Active', 'Not Setup', 'Overdue'];

const RentCollection = () => {
  const location = useLocation();

  const [loading, setLoading]           = useState(true);
  const [connectStatus, setConnectStatus] = useState('not_started');
  const [summary, setSummary]           = useState(null);
  const [tenants, setTenants]           = useState([]);
  const [selected, setSelected]         = useState(null);
  const [selectedBalance, setSelBal]    = useState(null);
  const [selectedLedger, setSelLedger]  = useState(null);
  const [selectedInvoices, setSelInv]   = useState([]);
  const [selectedLease, setSelLease]    = useState(null);
  const [filter, setFilter]             = useState('All');
  const [search, setSearch]             = useState('');
  const [error, setError]               = useState('');

  const loadConnectStatus = useCallback(async () => {
    const res = await paymentService.getConnectStatus();
    setConnectStatus(res.status);
    return res.status;
  }, []);

  const loadDashboard = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const status = await loadConnectStatus();
      if (status !== 'active') return;

      const [summaryRes, tenantsRes] = await Promise.all([
        paymentService.getInvoiceSummary(),
        tenantService.getAllTenants(),
      ]);
      setSummary(summaryRes);
      setTenants(tenantsRes);
    } catch (err) {
      setError('Failed to load rent collection data.');
    } finally {
      setLoading(false);
    }
  }, [loadConnectStatus]);

  useEffect(() => {
    loadDashboard();
  }, [loadDashboard]);

  // Handle Stripe Connect return
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get('connect') === 'return' || params.get('connect') === 'refresh') {
      loadConnectStatus().then(status => {
        setConnectStatus(status);
        if (status === 'active') loadDashboard();
      });
    }
  }, [location.search, loadConnectStatus, loadDashboard]);

  const loadTenantDetail = useCallback(async (tenant) => {
    setSelected(tenant);
    setSelBal(null);
    setSelLedger(null);
    setSelInv([]);
    setSelLease(null);
    try {
      const [bal, ledger, inv, leasesRes] = await Promise.all([
        paymentService.getTenantBalance(tenant._id),
        paymentService.getTenantLedger(tenant._id),
        paymentService.getTenantInvoices(tenant._id),
        apiClient.get('/api/leases', { params: { tenantId: tenant._id } }),
      ]);
      setSelBal(bal);
      setSelLedger(ledger);
      setSelInv(inv);
      const leases = leasesRes.data || [];
      setSelLease(leases.find(l => l.status === 'Active') || leases[0] || null);
    } catch (_) {}
  }, []);

  const handleRefresh = useCallback(() => {
    loadDashboard();
    if (selected) loadTenantDetail(selected);
  }, [loadDashboard, loadTenantDetail, selected]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  const filteredTenants = tenants
    .filter(t => {
      if (filter === 'ACH Active')  return !!t.stripeSubscriptionId;
      if (filter === 'Not Setup')   return !t.stripeSubscriptionId && t.achStatus !== 'verified';
      if (filter === 'Overdue')     return false; // would need per-tenant overdue data
      return true;
    })
    .filter(t => !search || t.name?.toLowerCase().includes(search.toLowerCase()));

  return (
    <div className="space-y-4 p-3 md:p-6">
      <div className="flex items-center gap-3">
        <CreditCard className="h-6 w-6 text-cyan-600" />
        <h2 className="text-2xl md:text-3xl font-bold tracking-tight">NivaPay</h2>
      </div>

      {error && (
        <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-800">
          <AlertCircle className="h-4 w-4 flex-shrink-0" />
          {error}
        </div>
      )}

      {connectStatus === 'not_started' ? (
        <LandlordBankSetup onConnected={loadDashboard} />
      ) : (
        <>
          {/* Summary cards */}
          {summary && (
            <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">Total Outstanding</CardTitle>
                  <DollarSign className="h-4 w-4 text-muted-foreground" />
                </CardHeader>
                <CardContent>
                  <div className={`text-2xl font-bold ${summary.outstanding > 0 ? 'text-red-600' : 'text-gray-900'}`}>
                    ${(summary.outstanding || 0).toLocaleString()}
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">Collected This Month</CardTitle>
                  <CheckCircle2 className="h-4 w-4 text-muted-foreground" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold text-green-600">
                    ${(summary.collectedThisMonth || 0).toLocaleString()}
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">Overdue</CardTitle>
                  <AlertCircle className="h-4 w-4 text-muted-foreground" />
                </CardHeader>
                <CardContent>
                  <div className={`text-2xl font-bold ${summary.overdueCount > 0 ? 'text-red-600' : 'text-gray-900'}`}>
                    {summary.overdueCount || 0}
                  </div>
                  <p className="text-xs text-muted-foreground">invoices</p>
                </CardContent>
              </Card>
              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">ACH Active</CardTitle>
                  <Users className="h-4 w-4 text-muted-foreground" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">{summary.achActiveTenants || 0}</div>
                  <p className="text-xs text-muted-foreground">tenants</p>
                </CardContent>
              </Card>
            </div>
          )}

          {/* Deposit account card */}
          <DepositAccountSection />

          {/* Split panel — mobile: one panel at a time; desktop: side-by-side */}
          <div className="flex border rounded-lg overflow-hidden bg-white md:h-[calc(100vh-3.5rem-260px)]">

            {/* Left: tenant list — hidden on mobile when detail is open */}
            <div className={`flex-col border-r flex-shrink-0 w-full md:w-auto
              ${selected ? 'hidden md:flex md:w-72' : 'flex'}`}
            >
              {/* Filter + search */}
              <div className="p-3 border-b space-y-2">
                <input
                  type="text"
                  placeholder="Search tenants…"
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500"
                />
                <div className="flex gap-1 flex-wrap">
                  {FILTERS.map(f => (
                    <button
                      key={f}
                      onClick={() => setFilter(f)}
                      className={`text-xs px-2 py-1 rounded-full border transition-colors ${filter === f ? 'bg-cyan-600 text-white border-cyan-600' : 'bg-white text-gray-600 border-gray-200 hover:border-cyan-300'}`}
                    >
                      {f}
                    </button>
                  ))}
                </div>
              </div>

              {/* Tenant rows */}
              <div className="flex-1 overflow-y-auto">
                {filteredTenants.length === 0 ? (
                  <div className="p-6 text-center text-sm text-muted-foreground">No tenants found.</div>
                ) : (
                  filteredTenants.map(t => (
                    <div
                      key={t._id}
                      role="button"
                      onClick={() => loadTenantDetail(t)}
                      className={`flex items-center justify-between px-4 py-3.5 border-b cursor-pointer hover:bg-gray-50 active:bg-gray-100 transition-colors ${selected?._id === t._id ? 'bg-cyan-50 border-l-2 border-l-cyan-500' : ''}`}
                    >
                      <div className="min-w-0 flex-1">
                        <div className="text-sm font-medium truncate">{t.name}</div>
                        <div className="text-xs text-muted-foreground truncate">{t.unitNumber}</div>
                      </div>
                      <div className="flex items-center gap-2 ml-2 flex-shrink-0">
                        <AchBadge achStatus={t.achStatus} subscriptionActive={!!t.stripeSubscriptionId} />
                        <ChevronRight className="h-4 w-4 text-gray-400" />
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>

            {/* Right: detail — hidden on mobile when no selection */}
            {selected ? (
              <div className="flex flex-col flex-1 min-w-0 overflow-hidden">
                {/* Mobile back button */}
                <div className="flex md:hidden items-center gap-2 px-4 py-2.5 border-b bg-gray-50 flex-shrink-0">
                  <button
                    onClick={() => setSelected(null)}
                    className="flex items-center gap-1.5 text-sm font-medium text-cyan-600"
                  >
                    <ArrowLeft className="h-4 w-4" />
                    All Tenants
                  </button>
                </div>
                <div className="flex-1 overflow-y-auto">
                  <TenantDetail
                    key={selected._id}
                    tenant={selected}
                    balance={selectedBalance}
                    ledger={selectedLedger}
                    invoices={selectedInvoices}
                    lease={selectedLease}
                    connectStatus={connectStatus}
                    onRefresh={handleRefresh}
                  />
                </div>
              </div>
            ) : (
              <div className="hidden md:flex flex-1 items-center justify-center text-muted-foreground">
                <div className="text-center">
                  <Users className="h-10 w-10 mx-auto mb-3 text-gray-300" />
                  <p className="text-sm">Select a tenant to view their account</p>
                </div>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default RentCollection;
